import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getSkillCatalog,
  installSkill,
  listAllSkills,
  listInstalledSkills,
  listSessionInvocations,
  runScheduledSkill,
  toggleSkill,
  updateScheduledSkillRun,
} from './skills-api'

export const skillKeys = {
  all: ['skills'] as const,
  installed: () => [...skillKeys.all, 'installed'] as const,
  every: () => [...skillKeys.all, 'all'] as const,
  catalog: () => [...skillKeys.all, 'catalog'] as const,
  invocations: (sessionId: string) => [...skillKeys.all, 'invocations', sessionId] as const,
}

export function useInstalledSkills() {
  return useQuery({
    queryKey: skillKeys.installed(),
    queryFn: () => listInstalledSkills(),
    staleTime: 30_000,
  })
}

export function useAllSkills() {
  return useQuery({
    queryKey: skillKeys.every(),
    queryFn: () => listAllSkills(),
    staleTime: 15_000,
  })
}

export function useSkillCatalog() {
  return useQuery({
    queryKey: skillKeys.catalog(),
    queryFn: () => getSkillCatalog(),
    staleTime: 15_000,
  })
}

export function useInstallSkill() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { source_repo: string; skill_name?: string }) => installSkill(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.installed() })
      void qc.invalidateQueries({ queryKey: skillKeys.every() })
      void qc.invalidateQueries({ queryKey: skillKeys.catalog() })
    },
  })
}

export function useToggleSkill() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => toggleSkill(id, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.installed() })
      void qc.invalidateQueries({ queryKey: skillKeys.every() })
      void qc.invalidateQueries({ queryKey: skillKeys.catalog() })
    },
  })
}

export function useUpdateScheduledSkill() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string
      patch: {
        enabled?: boolean
        schedule_cron?: string
        schedule_label?: string
        timezone?: string
      }
    }) => updateScheduledSkillRun(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.catalog() })
    },
  })
}

export function useRunScheduledSkill() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => runScheduledSkill(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.catalog() })
    },
  })
}

export function useSessionInvocations(sessionId: string | null) {
  return useQuery({
    queryKey: skillKeys.invocations(sessionId ?? ''),
    queryFn: () => listSessionInvocations(sessionId!),
    enabled: !!sessionId,
    staleTime: 5_000,
  })
}
